import * as Sentry from "@sentry/react";
import subscriptionConstants from "../constants/subscriptionConstants";
import { api } from "../api";
import localize, { LocKeys } from "../constants/localizations";
import { statusConstants } from "../constants/constants";
import {
	normalizeSubscriptions,
	normalizeMetadata,
	normalizeSubscription,
	isAbortError,
} from "../utils/subscription.util";
import { showNotification } from "./globalWidgetActions";
import { getItemFromLocalStorage, getToken } from "../utils/local-storage.util";
import { getPagination } from "../utils/table-filter";

const SUBSCRIPTION_ENDPOINT = "/company-subscription";
const SUBSCRIPTION_METADATA_ENDPOINT = "/company-subscription-metadata";
export const SUBSCRIPTION_METADATA_TYPES = [
	"service",
	"financial_impact",
	"invoicing_period",
	"subscription_duration",
];

const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 10;

// Helper: Convert immutable to plain object
const toPlainObject = (value) => {
	if (!value) return {};
	return value.toJS ? value.toJS() : value;
};

// Helper: Extract error message from API response
const getErrorMessage = (error) => {
	if (!error)
		return localize(LocKeys.SOMETHING_WENT_WRONG) || "Something went wrong";

	const data = error?.response?.data;
	return (
		data?.message ||
		data?.result ||
		data?.error ||
		error?.message ||
		localize(LocKeys.SOMETHING_WENT_WRONG) ||
		"Something went wrong"
	);
};

// Helper: Convert to positive integer or null
const toPositiveInt = (value) => {
	const num = Number(value);
	return Number.isFinite(num) && num > 0 ? num : null;
};

// Helper: Convert to number or null
const toNumber = (value) => {
	const num = Number(value);
	return Number.isFinite(num) ? num : null;
};

// Helper: Convert to array, handling immutable collections
const toArray = (value) => {
	if (!value) return [];
	if (Array.isArray(value)) return value.filter((v) => v != null && v !== "");
	if (value.toJS) return toArray(value.toJS());
	return [value].filter((v) => v != null && v !== "");
};

export const setSubscriptionFilters = (filters) => ({
	type: subscriptionConstants.SUBSCRIPTIONS_FILTERS_UPDATE,
	payload: filters || {},
});

export const resetSubscriptionStatus = () => ({
	type: subscriptionConstants.SUBSCRIPTIONS_RESET_STATUS,
});

/**
 * Request done (close modals)
 */
export const subscriptionRequestDone = () => ({
	type: subscriptionConstants.SUBSCRIPTIONS_REQUEST_DONE,
});

/**
 * Set subscription query parameters
 */
export const setSubscriptionQueryParameter = (data) => ({
	type: subscriptionConstants.SET_SUBSCRIPTION_QUERY_PARAMETER,
	data,
});

/**
 * Clear subscription query parameters
 */
export const clearSubscriptionQueryParameter = () => ({
	type: subscriptionConstants.CLEAR_SUBSCRIPTION_QUERY_PARAMETER,
});

/**
 * Clear subscription filters
 */
export const clearSubscriptionFilters = () => ({
	type: subscriptionConstants.SUBSCRIPTIONS_CLEAR_FILTERS,
});

/**
 * Fetch subscriptions with optional parameters
 * @param {Object} options Options object
 * @param {AbortSignal} options.signal Optional AbortSignal to cancel the request
 * @param {number} options.page Optional page number for pagination (1-based)
 * @param {string} options.searchQuery Optional search query string
 * @param {Object} options.searchFilters Optional search filters as key-value pairs
 * @param {Object} options.params Optional explicit params object to override other params
 * @returns {Function} Thunk function that performs the fetch and dispatches actions
 */
export const fetchSubscriptions =
	({
		signal,
		page,
		searchQuery,
		searchFilters,
		params,
		clientCompanyId,
		resellerCompanyId,
	} = {}) =>
	async (dispatch, getState) => {
		dispatch({ type: subscriptionConstants.SUBSCRIPTIONS_LIST_REQUEST });

		const state = getState();
		const subscription = state.get("subscription");
		const storedFilters = {
			...toPlainObject(subscription?.get("queryParameter")),
			...toPlainObject(subscription?.get("filters")),
		};

		// Merge incoming filters with stored filters
		const filters = { ...storedFilters, ...(searchFilters || {}) };

		// Get pagination from table or use defaults
		const tablePagination = state.getIn(["form", "tablePagination", "values"]);
		const { currentPage, currentLimit } = getPagination({
			tablePagination,
			page:
				toPositiveInt(page) ||
				toPositiveInt(storedFilters.page) ||
				DEFAULT_PAGE,
			limit: toPositiveInt(storedFilters.limit) || DEFAULT_LIMIT,
		});

		// Build API query params
		const query = {
			page: toPositiveInt(currentPage) || DEFAULT_PAGE,
			limit: toPositiveInt(currentLimit) || DEFAULT_LIMIT,
		};

		// Add search if provided
		const search = searchQuery || storedFilters.search || "";
		if (search.trim()) {
			query.search = search.trim();
		}

		// Handle status filter (convert to isActive boolean)
		const statusArray = toArray(filters.status).map((s) =>
			String(s).toLowerCase()
		);
		const status = statusArray[0];
		if (status === statusConstants.ACTIVE) {
			query.isActive = true;
		} else if (
			status === statusConstants.INACTIVE ||
			status === statusConstants.ARCHIVED
		) {
			query.isActive = false;
		}

		// Handle company IDs (support multiple, send as JSON array string)
		const companyIds = toArray(filters.companyId)
			.map(toNumber)
			.filter((n) => n !== null);
		if (companyIds.length > 0) {
			query.companyIds = JSON.stringify(companyIds);
		} // Handle other numeric filters (single value)
		const serviceId = toNumber(toArray(filters.serviceId)[0]);
		if (serviceId !== null) query.serviceId = serviceId;

		const financialImpactId = toNumber(toArray(filters.financialImpactId)[0]);
		if (financialImpactId !== null) query.financialImpactId = financialImpactId;

		const invoicingPeriodId = toNumber(toArray(filters.invoicingPeriodId)[0]);
		if (invoicingPeriodId !== null) query.invoicingPeriodId = invoicingPeriodId;

		const durationId = toNumber(toArray(filters.durationId)[0]);
		if (durationId !== null) query.durationId = durationId;

		// Merge with explicit params if provided
		const finalQuery = { ...query, ...(params || {}) };

		try {
			const response = await api({
				method: "get",
				url: SUBSCRIPTION_ENDPOINT,
				signal,
				params: {
					...finalQuery,
					...(clientCompanyId && { selectedCompanyId: clientCompanyId }),
					...(resellerCompanyId && { resellerCompanyId: resellerCompanyId }),
				},
				headers: {
					Authorization: "Bearer " + getToken(),
					"Content-Type": "application/json",
				},
			});

			const payload = response?.data || {};
			if (payload?.status === false) {
				throw new Error(getErrorMessage({ response: { data: payload } }));
			}

			const { list, meta } = normalizeSubscriptions(payload);
			dispatch({
				type: subscriptionConstants.SUBSCRIPTIONS_LIST_SUCCESS,
				payload: { list, meta, raw: payload },
			});

			return list;
		} catch (error) {
			if (isAbortError(error)) return [];

			const message = getErrorMessage(error);
			dispatch({
				type: subscriptionConstants.SUBSCRIPTIONS_LIST_FAILURE,
				error: message,
			});
			dispatch(subscriptionActionFailure(message));
			Sentry.captureException(error);
			dispatch(showNotification({ message, severity: "error" }));
			throw error;
		}
	};

export const fetchSubscriptionMetadata =
	(type, { force = false, signal } = {}) =>
	async (dispatch, getState) => {
		if (!type) {
			return [];
		}

		const normalizedType = String(type).toLowerCase();
		if (!SUBSCRIPTION_METADATA_TYPES.includes(normalizedType)) {
			return [];
		}

		const existing = getState()?.getIn([
			"subscription",
			"metadata",
			normalizedType,
		]);
		if (!force && existing && existing.size > 0) {
			return existing.toJS();
		}

		dispatch({
			type: subscriptionConstants.SUBSCRIPTIONS_METADATA_REQUEST,
			metaType: normalizedType,
		});

		try {
			const response = await api({
				method: "get",
				url: `${SUBSCRIPTION_METADATA_ENDPOINT}?type=${normalizedType}`,
				signal,
				headers: {
					Authorization: "Bearer " + getToken(),
					"Content-Type": "application/json",
				},
			});

			const payload = response?.data || {};
			if (payload?.status === false) {
				const envelopeError = new Error(
					getErrorMessage({ response: { data: payload } })
				);
				envelopeError.response = { data: payload };
				throw envelopeError;
			}
			const data = normalizeMetadata(payload);

			dispatch({
				type: subscriptionConstants.SUBSCRIPTIONS_METADATA_SUCCESS,
				metaType: normalizedType,
				payload: data,
			});
			return data;
		} catch (error) {
			if (isAbortError(error)) {
				return [];
			}

			const message = getErrorMessage(error);
			dispatch({
				type: subscriptionConstants.SUBSCRIPTIONS_METADATA_FAILURE,
				metaType: normalizedType,
				error: message,
			});
			Sentry.captureException(error);
			dispatch(
				showNotification({
					message,
					severity: "error",
				})
			);
			throw error;
		}
	};

const buildCreatePayload = (data = {}) => {
	const body = {};
	if (data.companyId !== undefined && data.companyId !== null) {
		body.companyId = Number(data.companyId);
	}
	if (data.amountMonthly !== undefined && data.amountMonthly !== null) {
		body.amountMonthly = Number(data.amountMonthly);
	}

	// Handle optional fields - explicitly check for null/undefined to allow 0 values
	if (data.serviceId !== null && data.serviceId !== undefined) {
		body.serviceId = data.serviceId;
	}
	if (data.financialImpactId !== null && data.financialImpactId !== undefined) {
		body.financialImpactId = data.financialImpactId;
	}
	if (data.durationId !== null && data.durationId !== undefined) {
		body.durationId = data.durationId;
	}
	if (data.invoicingPeriodId !== null && data.invoicingPeriodId !== undefined) {
		body.invoicingPeriodId = data.invoicingPeriodId;
	}
	if (data.notes !== null && data.notes !== undefined && data.notes !== "") {
		body.notes = data.notes;
	}
	if (data.startDate !== null && data.startDate !== undefined) {
		body.startDate = data.startDate;
	}
	if (data.endDate !== null && data.endDate !== undefined) {
		body.endDate = data.endDate;
	}
	if (
		data.revenueShares &&
		Array.isArray(data.revenueShares) &&
		data.revenueShares.length > 0
	) {
		body.revenueShares = data.revenueShares;
	}

	return body;
};

export const createSubscription = (formData) => async (dispatch) => {
	dispatch({ type: subscriptionConstants.SUBSCRIPTIONS_CREATE_REQUEST });

	const payload = buildCreatePayload(formData);

	try {
		const response = await api({
			method: "post",
			url: SUBSCRIPTION_ENDPOINT,
			data: payload,
			headers: {
				Authorization: "Bearer " + getToken(),
				"Content-Type": "application/json",
			},
		});

		const body = response?.data || {};
		if (body?.status === false) {
			const envelopeError = new Error(
				getErrorMessage({ response: { data: body } })
			);
			envelopeError.response = { data: body };
			throw envelopeError;
		}
		const candidate = Array.isArray(body?.data)
			? body.data[0]
			: Array.isArray(body?.result)
				? body.result[0]
				: body.data || body.result || body;
		const entity = normalizeSubscription(candidate || {});
		const successLabel =
			(localize(LocKeys.SUBSCRIPTION) || "Subscription") +
			" " +
			(localize(LocKeys.CREATED) || "created successfully");

		dispatch({
			type: subscriptionConstants.SUBSCRIPTIONS_CREATE_SUCCESS,
			payload: entity,
			message: successLabel,
		});

		dispatch(setSubscriptionSuccessMessage(successLabel));
		dispatch(subscriptionActionSuccess(successLabel));

		dispatch(
			showNotification({
				message: successLabel,
				severity: "success",
			})
		);

		return entity;
	} catch (error) {
		if (isAbortError(error)) {
			return null;
		}
		const message = getErrorMessage(error);
		dispatch({
			type: subscriptionConstants.SUBSCRIPTIONS_CREATE_FAILURE,
			error: message,
		});
		dispatch(subscriptionActionFailure(message));
		Sentry.captureException(error);
		dispatch(
			showNotification({
				message,
				severity: "error",
			})
		);
		throw error;
	}
};

export const fetchSubscriptionById =
	(id, { signal, suppressNotification = false } = {}) =>
	async (dispatch) => {
		if (id === null || id === undefined) {
			return null;
		}

		const numericId = Number(id);
		if (!Number.isFinite(numericId)) {
			return null;
		}

		try {
			const response = await api({
				method: "get",
				url: `${SUBSCRIPTION_ENDPOINT}/${numericId}`,
				signal,
				headers: {
					Authorization: "Bearer " + getToken(),
					"Content-Type": "application/json",
				},
			});

			const body = response?.data || {};
			if (body?.status === false) {
				const envelopeError = new Error(
					getErrorMessage({ response: { data: body } })
				);
				envelopeError.response = { data: body };
				throw envelopeError;
			}

			const candidate = Array.isArray(body?.data)
				? body.data[0]
				: Array.isArray(body?.result)
					? body.result[0]
					: body.data || body.result || body;

			return normalizeSubscription(candidate || {});
		} catch (error) {
			if (isAbortError(error)) {
				return null;
			}

			const message = getErrorMessage(error);
			Sentry.captureException(error);
			if (!suppressNotification) {
				dispatch(subscriptionActionFailure(message));
				dispatch(
					showNotification({
						message,
						severity: "error",
					})
				);
			}

			throw error;
		}
	};

export const setCurrentSubscription = (subscription) => ({
	type: subscriptionConstants.SUBSCRIPTIONS_SET_CURRENT,
	payload: subscription,
});

export const clearCurrentSubscription = () => ({
	type: subscriptionConstants.SUBSCRIPTIONS_CLEAR_CURRENT,
});

export const hydrateSubscription = (id) => async (dispatch, getState) => {
	if (!id && id !== 0) {
		dispatch(clearCurrentSubscription());
		return null;
	}

	const numericId = Number(id);
	if (!Number.isFinite(numericId)) {
		dispatch(clearCurrentSubscription());
		return null;
	}
	const state = getState();
	const list = state?.getIn(["subscription", "list"]);

	let found = null;
	if (list && list.find) {
		found = list.find((item) => Number(item.get("id")) === numericId);
	}

	if (found) {
		const data = found.toJS ? found.toJS() : found;
		dispatch(setCurrentSubscription(data));
		return data;
	}

	try {
		const entity = await dispatch(
			fetchSubscriptionById(numericId, { suppressNotification: true })
		);
		if (entity) {
			dispatch(setCurrentSubscription(entity));
			return entity;
		}
	} catch (error) {
		// fetchSubscriptionById already surfaces errors; fall through to clear state.
	}

	dispatch(clearCurrentSubscription());
	return null;
};

export const updateSubscriptionStatus =
	(ids, status) => async (dispatch, getState) => {
		const endpoint = "/company-subscription-status";

		// Convert IDs to array of numbers
		const normalizedIds = toArray(ids)
			.map(toNumber)
			.filter((n) => n !== null);
		if (normalizedIds.length === 0) {
			return null;
		}

		const normalizedStatus =
			typeof status === "string" && status.trim().length > 0
				? status.trim().toLowerCase()
				: statusConstants.ACTIVE;

		let successMessage =
			localize(LocKeys.ITEM_ACTIVE_SUCCESS, [
				localize(LocKeys.SUBSCRIPTION) || "Subscription",
			]) || "Subscription activated.";

		switch (normalizedStatus) {
			case statusConstants.ARCHIVED:
				successMessage =
					localize(LocKeys.ITEM_ARCHIVE_SUCCESS, [
						localize(LocKeys.SUBSCRIPTION) || "Subscription",
					]) || "Subscription archived.";
				break;

			case statusConstants.INACTIVE:
				successMessage = localize(LocKeys.STATUS_UPDATED) || "Status updated.";
				break;

			default:
				break;
		}

		dispatch({
			type: subscriptionConstants.SUBSCRIPTIONS_UPDATE_REQUEST,
			meta: { status: normalizedStatus },
		});

		try {
			const response = await api({
				method: "put",
				url: endpoint,
				headers: {
					Authorization: "Bearer " + getToken(),
					"Content-Type": "application/json",
				},
				data: { ids: normalizedIds, status: status },
			});

			const body = response?.data || {};
			if (body?.status === false) {
				const envelopeError = new Error(
					getErrorMessage({ response: { data: body } })
				);
				envelopeError.response = { data: body };
				throw envelopeError;
			}

			let updatedEntity = null;
			if (Array.isArray(body?.result) && body.result.length === 1) {
				updatedEntity = normalizeSubscription(body.result[0]);
			} else if (body?.result && typeof body.result === "object") {
				updatedEntity = normalizeSubscription(body.result);
			} else if (Array.isArray(body?.data) && body.data.length === 1) {
				updatedEntity = normalizeSubscription(body.data[0]);
			}

			dispatch({
				type: subscriptionConstants.SUBSCRIPTIONS_UPDATE_SUCCESS,
				payload: updatedEntity,
				message: successMessage,
				meta: { status: normalizedStatus },
			});

			dispatch(setSubscriptionSuccessMessage(successMessage));
			dispatch(subscriptionActionSuccess(successMessage));

			dispatch(
				showNotification({
					message: successMessage,
					severity: "success",
				})
			);

			await dispatch(fetchSubscriptions());
			await dispatch(fetchSubscriptionStats());

			return updatedEntity;
		} catch (error) {
			if (isAbortError(error)) {
				dispatch({
					type: subscriptionConstants.SUBSCRIPTIONS_UPDATE_FAILURE,
					error: "Request canceled.",
				});
				return null;
			}

			const message = getErrorMessage(error);
			dispatch({
				type: subscriptionConstants.SUBSCRIPTIONS_UPDATE_FAILURE,
				error: message,
			});
			dispatch(subscriptionActionFailure(message));
			Sentry.captureException(error);
			dispatch(
				showNotification({
					message,
					severity: "error",
				})
			);
			throw error;
		}
	};

/**
 * Retrieve subscription statistics (MRR, MRC, total count)
 * Reads filters from Redux state if no options provided
 */
export const fetchSubscriptionStats =
	(options = {}) =>
	async (dispatch, getState) => {
		dispatch({ type: subscriptionConstants.SUBSCRIPTIONS_STATS_REQUEST });

		const customer = getItemFromLocalStorage("Customer");
		const resellerCompanyId = getItemFromLocalStorage("Reseller");

		try {
			// Read from state if no options provided
			const state = getState();
			const filters = state.getIn(["subscription", "filters"]);
			const filtersPlain = toPlainObject(filters);

			const params = {};

			// Use provided options or fall back to state filters
			const search = options.search || filtersPlain.search;
			if (search) params.search = search;

			const isActive =
				options.isActive !== undefined
					? options.isActive
					: filtersPlain.isActive;
			if (isActive !== undefined) params.isActive = isActive;

			// Handle company IDs (support multiple, send as JSON array string)
			const companyId = options.companyId || filtersPlain.companyId;
			if (companyId) {
				const companyIds = toArray(companyId)
					.map(toNumber)
					.filter((n) => n !== null);
				if (companyIds.length > 0) {
					params.companyIds = JSON.stringify(companyIds);
				}
			}
			const serviceId =
				options.serviceId !== undefined
					? options.serviceId
					: filtersPlain.serviceId;
			if (serviceId !== undefined && serviceId !== null) {
				params.serviceId = serviceId;
			}

			const invoicingPeriodId =
				options.invoicingPeriodId !== undefined
					? options.invoicingPeriodId
					: filtersPlain.invoicingPeriodId;
			if (invoicingPeriodId !== undefined && invoicingPeriodId !== null) {
				params.invoicingPeriodId = invoicingPeriodId;
			}

			const response = await api({
				method: "GET",
				url: "/company-subscription-stats",
				params: {
					...params,
					...(customer && { selectedCompanyId: customer }),
					...(resellerCompanyId && { resellerCompanyId }),
				},
				signal: options.signal,
				headers: {
					Authorization: `Bearer ${getToken()}`,
					"Content-Type": "application/json",
				},
			});

			if (!response.data.status) {
				throw new Error(
					response.data.result || "Failed to fetch subscription stats"
				);
			}

			dispatch({
				type: subscriptionConstants.SUBSCRIPTIONS_STATS_SUCCESS,
				payload: response.data.result,
			});

			return response.data.result;
		} catch (error) {
			if (isAbortError(error)) return null;

			const errorMessage = getErrorMessage(error);
			dispatch({
				type: subscriptionConstants.SUBSCRIPTIONS_STATS_FAILURE,
				error: errorMessage,
			});
			Sentry.captureException(error);
			dispatch(
				showNotification({
					message: errorMessage,
					severity: "error",
				})
			);

			return null;
		}
	};

export const setSubscriptionSuccessMessage = (data) => ({
	type: subscriptionConstants.SUBSCRIPTIONS_SET_SUCCESS_MESSAGE,
	data,
});

export const subscriptionActionSuccess = (message = null) => ({
	type: subscriptionConstants.SUBSCRIPTIONS_ACTION_SUCCESS,
	message,
});

export const subscriptionActionFailure = (error) => ({
	type: subscriptionConstants.SUBSCRIPTIONS_ACTION_FAILURE,
	error,
});

export const clearSubscriptionRequestState = () => ({
	type: subscriptionConstants.SUBSCRIPTIONS_CLEAR_REQUEST_STATE,
});
